import type { BatteryFigures, BatteryHistory, EnergyFigures, EnergyHistory } from "@spf/contracts";
import { Card } from "../Card.js";
import { DataList, DataRow } from "../Page.js";
import { Failed, Loading } from "../ViewState.js";
import { formatKwh } from "../../lib/energy.js";
import { formatMoney, formatPercent } from "../../lib/format.js";
import type { Async } from "../../lib/useAsync.js";

/**
 * What the grid cost over the period chosen above, and what the battery's
 * energy cost -- `REQUIREMENTS.md` section 1 job 2, import cost and grid
 * charging kept apart, the second including its round-trip loss.
 *
 * Two queries feed it: the ledger's energy for the grid, and the battery
 * endpoint for where the battery's energy came from (docs/DECISIONS/0033).
 * Every figure is the API's; nothing is divided or summed here.
 */
export function CostCard({
  period,
  energy,
  battery,
  currency,
  onRetry,
}: {
  /** What the period's total is called, such as "Year to date". */
  period: string;
  energy: Async<EnergyHistory>;
  battery: Async<BatteryHistory>;
  currency: string;
  onRetry: () => void;
}) {
  return (
    <Card title="What it cost">
      {energy.kind === "loading" || battery.kind === "loading" ? (
        <Loading label="Loading the cost…" />
      ) : energy.kind === "error" ? (
        <Failed message={energy.message} onRetry={onRetry} />
      ) : battery.kind === "error" ? (
        <Failed message={battery.message} onRetry={onRetry} />
      ) : energy.value.totals.recordedSeconds === 0 ? (
        <p className="py-8 text-center text-sm text-ink-muted">
          Nothing was recorded in this period, so there is nothing to price.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Grid period={period} totals={energy.value.totals} currency={currency} />
          <Battery period={period} totals={battery.value.totals} currency={currency} />
        </div>
      )}
    </Card>
  );
}

function Grid({
  period,
  totals,
  currency,
}: {
  period: string;
  totals: EnergyFigures;
  currency: string;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h4 className="text-sm font-semibold text-ink">{`The grid, ${period.toLowerCase()}`}</h4>
      <DataList columns={1}>
        <DataRow label="Imported">{formatKwh(totals.gridImportKwh)}</DataRow>
        <DataRow label="What the import cost">
          {formatMoney(totals.gridImportCost, currency)}
        </DataRow>
        <DataRow label="Drawn to charge the battery">{formatKwh(totals.gridChargeKwh)}</DataRow>
        <DataRow label="What that charging cost">
          {formatMoney(totals.gridChargeCost, currency)}
        </DataRow>
      </DataList>
      <p className="text-xs text-ink-muted">{gridNote(totals)}</p>
    </section>
  );
}

function gridNote(totals: EnergyFigures): string {
  if (totals.gridImportCost === null) {
    return "Part of this period has no tariff, so its cost is not shown. An administrator can add an earlier rate under Settings.";
  }
  if (totals.gridChargeKwh === null) {
    return "Part of this period was recorded before the grid charger's power was, so what charging drew is not known for it.";
  }
  return "The charging is what the meter billed for it, the charger's own loss included. It is part of the import, not added to it.";
}

function Battery({
  period,
  totals,
  currency,
}: {
  period: string;
  totals: BatteryFigures;
  currency: string;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h4 className="text-sm font-semibold text-ink">{`The battery, ${period.toLowerCase()}`}</h4>
      <DataList columns={1}>
        <DataRow label="Given out">{formatKwh(totals.dischargedKwh)}</DataRow>
        <DataRow label="Of it, charged from the grid">{formatKwh(totals.fromGridKwh)}</DataRow>
        <DataRow label="Of it, charged by the panels">{formatKwh(totals.fromSolarKwh)}</DataRow>
        <DataRow label="Of it, source not known">{formatKwh(totals.unattributedKwh)}</DataRow>
        <DataRow label="What it cost to put in">
          {formatMoney(totals.dischargedCost, currency)}
        </DataRow>
        <DataRow label="Per kWh from the grid, once back out">
          {formatMoney(totals.gridCostPerKwh, currency)}
        </DataRow>
        <DataRow label="Round-trip efficiency">{formatPercent(totals.roundTripEfficiency)}</DataRow>
      </DataList>
      <p className="text-xs text-ink-muted">{roundTripNote(totals)}</p>
    </section>
  );
}

function roundTripNote(totals: BatteryFigures): string {
  const source =
    "Energy of unknown source was in the battery when records began, or arrived across a gap in them.";
  if (totals.roundTripEfficiency !== null) {
    return `${source} The round trip is what came back out of what went in.`;
  }
  return (
    `${source} The round trip is measured once ${formatKwh(totals.requiredChargeKwh)} ` +
    `has been charged in the period, ten times the battery's capacity; ` +
    `${formatKwh(totals.chargedKwh ?? 0)} has been so far.`
  );
}
